import { HttpErrorResponse } from '@angular/common/http';
import type { IngestDuplicateCheckResponse } from '../../../../domain/src/lib/ingest/ingest-project.model';

export const INGEST_DUPLICATE_REQUIRES_CONFIRMATION = 'DUPLICATE_REQUIRES_CONFIRMATION';

export interface IngestDuplicateCheckTarget {
  filename: string;
}

function toBooleanFlag(value: unknown, defaultValue = false): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const v = value.trim().toLowerCase();
    if (['true', '1', 'yes', 'si', 'sí'].includes(v)) return true;
    if (['false', '0', 'no'].includes(v)) return false;
  }
  return defaultValue;
}

function readFiniteVersion(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

export function normalizeIngestDuplicateCheck(raw: unknown): IngestDuplicateCheckResponse {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return { exists: false, requires_confirmation: false };
  }
  const r = raw as Record<string, unknown>;
  const current_version = readFiniteVersion(r['current_version']);
  const new_version = readFiniteVersion(r['new_version']);
  const version_number = readFiniteVersion(r['version_number']);
  return {
    exists: toBooleanFlag(r['exists']),
    requires_confirmation: toBooleanFlag(r['requires_confirmation']),
    current_version,
    new_version,
    version_number,
    logical_name: typeof r['logical_name'] === 'string' ? r['logical_name'] : null,
    previous_ingested_at:
      typeof r['previous_ingested_at'] === 'string' ? r['previous_ingested_at'] : null,
    filename: typeof r['filename'] === 'string' ? r['filename'] : null,
    message: typeof r['message'] === 'string' ? r['message'] : null,
    error_code: typeof r['error_code'] === 'string' ? r['error_code'] : null,
  };
}

/** Resuelve versiones con fallback a campos legacy del API. */
export function resolveIngestDuplicateVersions(check: IngestDuplicateCheckResponse): {
  currentVersion: number;
  newVersion: number;
} {
  const currentVersion =
    check.current_version ?? check.version_number ?? 1;
  const newVersion = check.new_version ?? currentVersion + 1;
  return { currentVersion, newVersion };
}

/** Nombres a consultar en check-duplicate antes del upload. */
export function resolveIngestDuplicateCheckTargets(
  entries: ReadonlyArray<{ relativePath: string; source: 'loose' | 'folder' }>,
): IngestDuplicateCheckTarget[] {
  const hasFolder = entries.some((entry) => entry.source === 'folder');
  const names = new Set<string>();

  if (hasFolder) {
    for (const entry of entries) {
      if (entry.source !== 'folder') continue;
      const parts = entry.relativePath.split('/').filter(Boolean);
      if (parts.length > 0) {
        names.add(parts[0]);
      }
    }
    return [...names].map((filename) => ({ filename }));
  }

  for (const entry of entries) {
    const parts = entry.relativePath.split('/').filter(Boolean);
    const leaf = parts[parts.length - 1] || entry.relativePath.trim();
    if (leaf) {
      names.add(leaf);
    }
  }
  return [...names].map((filename) => ({ filename }));
}

/**
 * Copy del modal de duplicado (#0000044).
 * No usa `message` del API: el texto lo arma front con campos estructurados.
 */
export function formatIngestDuplicateConfirmMessage(
  check: IngestDuplicateCheckResponse,
  filename: string,
): string {
  const displayName = check.logical_name?.trim() || filename;
  const { currentVersion, newVersion } = resolveIngestDuplicateVersions(check);

  if (currentVersion === 1) {
    return `«${displayName}» ya está en la bóveda.\n\n¿Desea cargar una nueva versión?`;
  }

  return `Ya existe la versión v${currentVersion} de «${displayName}».\n\n¿Desea crear la versión v${newVersion}?`;
}

export function ingestDuplicateCheckRequiresConfirmation(
  check: IngestDuplicateCheckResponse,
): boolean {
  return check.exists === true && check.requires_confirmation === true;
}

function readIngestErrorObject(err: unknown): Record<string, unknown> | null {
  if (!(err instanceof HttpErrorResponse)) return null;
  const body = err.error;
  if (body && typeof body === 'object' && !Array.isArray(body)) {
    return body as Record<string, unknown>;
  }
  if (typeof body === 'string' && body.trim().startsWith('{')) {
    try {
      const parsed = JSON.parse(body) as Record<string, unknown>;
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
      return null;
    }
  }
  return null;
}

export function isIngestDuplicateRequiresConfirmationError(err: unknown): boolean {
  if (!(err instanceof HttpErrorResponse)) return false;
  const body = readIngestErrorObject(err);
  if (!body) return false;
  const code = body['error_code'];
  return (
    err.status === 409 &&
    typeof code === 'string' &&
    code.trim().toUpperCase() === INGEST_DUPLICATE_REQUIRES_CONFIRMATION
  );
}

export function ingestDuplicateCheckFromUploadError(
  err: unknown,
  fallbackFilename: string,
): IngestDuplicateCheckResponse | null {
  if (!isIngestDuplicateRequiresConfirmationError(err)) return null;
  const body = readIngestErrorObject(err);
  if (!body) {
    return {
      exists: true,
      requires_confirmation: true,
      filename: fallbackFilename,
    };
  }
  return normalizeIngestDuplicateCheck({
    ...body,
    exists: true,
    requires_confirmation: true,
    filename:
      typeof body['filename'] === 'string' ? body['filename'] : fallbackFilename,
  });
}
